import React, { useState, useEffect, useRef, useCallback, useMemo, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { LanguageProvider, useTranslation } from './contexts/LanguageContext';
import { ToastProvider, useToast } from './context/ToastContext';
import { SettingsSheetProvider } from './context/SettingsSheetContext';
import { DownloadManagerProvider, useDownloads } from './context/DownloadManagerContext';
import { PlayerProvider, usePlayer } from './context/PlayerContext';
import Header from './components/Header';
import InstallPrompt from './components/InstallPrompt';
import UpdatePrompt from './components/UpdatePrompt';
import DownloadStrip from './components/DownloadStrip';
import { usePodcasts } from './hooks/usePodcasts';
import { usePlaylist } from './hooks/usePlaylist';
import { initGA, trackPageView } from './utils/analytics';
import { usePwaInstallAnalytics } from './hooks/usePwaInstallAnalytics';
import './styles/App.css';

const PodcastList = lazy(() => import('./components/PodcastList'));
const PlaylistManager = lazy(() => import('./components/PlaylistManager'));
const HeroSection = lazy(() => import('./components/HeroSection'));
const BrowsePage = lazy(() => import('./pages/BrowsePage'));
const SearchPage = lazy(() => import('./pages/SearchPage'));
const EpisodeDetailPage = lazy(() => import('./pages/EpisodeDetailPage'));
const BottomPlayerBar = lazy(() => import('./components/BottomPlayerBar'));
const ExpandedPlayer = lazy(() => import('./components/ExpandedPlayer'));
const AdBanner = lazy(() => import('./components/AdBanner'));

// Owns podcasts/search/filter/queue state and mounts the DownloadManager and
// Player providers. PlayerProvider needs `currentPlaylist`/`filteredPodcasts`
// as props (owned here), so it is constructed in this component's JSX — which
// means THIS component can't call usePlayer() itself (it's the provider's
// ancestor, not a descendant). AppShell, rendered as the provider's child,
// is what actually reads player state and renders the routes/player UI.
function AppContent() {
  const { podcasts, summary, loading, searchPodcasts, filterByDate, filterByParticipants, getUniqueParticipants } = usePodcasts();
  const { currentPlaylist, addToPlaylist, removeFromPlaylist, clearPlaylist, moveInPlaylist } = usePlaylist();
  const allParticipants = useMemo(() => getUniqueParticipants(), [podcasts]);
  const [filteredPodcasts, setFilteredPodcasts] = useState([]);

  // Keep a ref to latest podcasts so handleSearch never uses a stale closure
  const podcastsRef = useRef(podcasts);
  podcastsRef.current = podcasts;

  useEffect(() => { setFilteredPodcasts(podcasts); }, [podcasts]);

  // Google Analytics
  useEffect(() => { initGA(); trackPageView('home', 'Podcast Archive - Home'); }, []);

  // Report PWA-install signals to GA4 (display_mode user property + the
  // `appinstalled` event). Mounted once here in the single app-content root.
  usePwaInstallAnalytics();

  const handleSearch = (searchTerm, dateRange, participants) => {
    let results = podcastsRef.current;
    if (searchTerm) results = searchPodcasts(searchTerm);
    if (dateRange && (dateRange.start || dateRange.end)) results = filterByDate(results, dateRange);
    if (participants && participants.length > 0) results = filterByParticipants(results, participants);
    setFilteredPodcasts(results);
  };

  return (
    <ToastProvider>
      <SettingsSheetProvider>
        <DownloadManagerProvider episodes={podcasts}>
          <PlayerProvider playlist={currentPlaylist} fallbackList={filteredPodcasts}>
            <AppShell
              podcasts={podcasts}
              summary={summary}
              loading={loading}
              searchPodcasts={searchPodcasts}
              allParticipants={allParticipants}
              filteredPodcasts={filteredPodcasts}
              handleSearch={handleSearch}
              currentPlaylist={currentPlaylist}
              addToPlaylist={addToPlaylist}
              removeFromPlaylist={removeFromPlaylist}
              clearPlaylist={clearPlaylist}
              moveInPlaylist={moveInPlaylist}
            />
          </PlayerProvider>
        </DownloadManagerProvider>
      </SettingsSheetProvider>
    </ToastProvider>
  );
}

// Descendant of PlayerProvider: owns routes, deep-link parsing, share, and
// shuffle — everything that needs both the podcasts/queue state from
// AppContent and the player state/actions from usePlayer().
function AppShell({
  podcasts,
  summary,
  loading,
  searchPodcasts,
  allParticipants,
  filteredPodcasts,
  handleSearch,
  currentPlaylist,
  addToPlaylist,
  removeFromPlaylist,
  clearPlaylist,
  moveInPlaylist,
}) {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const { currentAudio, currentTime, isPlaying, isPlayerExpanded, playPodcast, togglePlay, seek } = usePlayer();
  // Same pendingCount/errorCount fields (and the same condition, just not
  // negated) that DownloadStrip.js reads to decide its own null-vs-render —
  // read here from the same useDownloads() call so the .App class and the
  // strip's visibility can never disagree.
  const { pendingCount, errorCount } = useDownloads();
  const hasDownloads = pendingCount > 0 || errorCount > 0;

  // Deep-link: load episode from URL params (?episode=...&t=...)
  useEffect(() => {
    if (!podcasts.length) return;
    const params = new URLSearchParams(window.location.search);
    const episodeId = params.get('episode');
    if (!episodeId) return;
    const podcast = podcasts.find(p => p.id === episodeId);
    if (!podcast) return;
    playPodcast(podcast);
    const t = parseInt(params.get('t'), 10);
    if (t > 0) seek(t);
    window.history.replaceState({}, '', window.location.pathname);
  }, [podcasts]);

  const handleShareEpisode = useCallback(() => {
    if (!currentAudio) return;
    // Reads the context's `currentTime` (updated on 'timeupdate', ~4/sec)
    // rather than the live audio element directly — audioRef is now private
    // to PlayerProvider. Same granularity a share link needs.
    const time = Math.floor(currentTime || 0);
    const params = time > 0 ? `episode=${currentAudio.id}&t=${time}` : `episode=${currentAudio.id}`;
    const shareUrl = `${window.location.origin}/?${params}`;
    if (navigator.share) {
      navigator.share({ title: t('shareEpisodeTitle'), text: t('shareEpisodeText'), url: shareUrl });
    } else {
      navigator.clipboard.writeText(shareUrl)
        .then(() => showToast(t('toast.linkCopied')))
        .catch(() => showToast(t('toast.copyFailed', { url: shareUrl })));
    }
  }, [currentAudio, currentTime, t, showToast]);

  const handleShufflePlay = () => {
    if (filteredPodcasts.length === 0) return;
    const random = filteredPodcasts[Math.floor(Math.random() * filteredPodcasts.length)];
    // Re-picking the already-current episode must not go through playPodcast:
    // its same-id branch TOGGLES, so if it's already playing that would
    // silently pause it (a regression vs. the original App.js, whose
    // setCurrentAudio(sameObject) was a React Object.is no-op that just kept
    // playing). Only resume if it was paused; otherwise leave it alone.
    if (currentAudio?.id === random.id) {
      if (!isPlaying) togglePlay();
    } else {
      playPodcast(random);
    }
  };

  return (
    <Router>
      <div className={`App ${currentAudio ? 'has-player' : ''} ${hasDownloads ? 'has-downloads' : ''}`}>
        <Header />
        <InstallPrompt />
        <UpdatePrompt />

        <main className="main-content">
          <div className="main-content-inner">
            <Routes>
              <Route
                path="/"
                element={
                  <>
                    <Suspense fallback={<div className="loading-placeholder">Loading...</div>}>
                      <HeroSection
                        summary={summary}
                        onSearch={handleSearch}
                        onShufflePlay={handleShufflePlay}
                        allParticipants={allParticipants}
                      />
                    </Suspense>
                    <Suspense fallback={null}>
                      <AdBanner slot="header" format="auto" className="ad-banner-header" />
                    </Suspense>
                    <Suspense fallback={<div className="loading-placeholder">Loading episodes...</div>}>
                      <PodcastList
                        podcasts={filteredPodcasts}
                        loading={loading}
                        onAddToPlaylist={addToPlaylist}
                      />
                    </Suspense>
                  </>
                }
              />
              <Route
                path="/browse"
                element={
                  <Suspense fallback={<div className="loading-placeholder">Loading...</div>}>
                    <BrowsePage
                      podcasts={podcasts}
                      loading={loading}
                      onAddToPlaylist={addToPlaylist}
                    />
                  </Suspense>
                }
              />
              <Route
                path="/search"
                element={
                  <Suspense fallback={<div className="loading-placeholder">Loading...</div>}>
                    <SearchPage
                      podcasts={podcasts}
                      searchPodcasts={searchPodcasts}
                      loading={loading}
                      onAddToPlaylist={addToPlaylist}
                    />
                  </Suspense>
                }
              />
              <Route
                path="/queue"
                element={
                  <Suspense fallback={<div className="loading-placeholder">Loading queue...</div>}>
                    <PlaylistManager
                      playlist={currentPlaylist}
                      onRemove={removeFromPlaylist}
                      onClear={clearPlaylist}
                      onReorder={moveInPlaylist}
                    />
                  </Suspense>
                }
              />
              <Route
                path="/episode/:id"
                element={
                  <Suspense fallback={<div className="loading-placeholder">Loading...</div>}>
                    <EpisodeDetailPage
                      podcasts={podcasts}
                      loading={loading}
                      onAddToPlaylist={addToPlaylist}
                    />
                  </Suspense>
                }
              />
            </Routes>
          </div>
        </main>

        {/* Global download-queue strip — self-hides via pendingCount/errorCount.
            Mounted directly above BottomPlayerBar; its own CSS (has-player
            aware) keeps it sitting above the player bar and, at mobile
            widths, above the bottom tab bar too. */}
        <DownloadStrip />

        {/* Bottom player bar — reads shared audio state from usePlayer() */}
        {currentAudio && !isPlayerExpanded && (
          <Suspense fallback={null}>
            <BottomPlayerBar onShare={handleShareEpisode} />
          </Suspense>
        )}

        {/* Expanded player overlay — reads shared audio state from usePlayer() */}
        {currentAudio && isPlayerExpanded && (
          <Suspense fallback={null}>
            <ExpandedPlayer onShare={handleShareEpisode} />
          </Suspense>
        )}
      </div>
    </Router>
  );
}

function App() {
  return (
    <LanguageProvider>
      <AppContent />
    </LanguageProvider>
  );
}

export default App;
